import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import config from 'application-config';

import { MODAL } from 'admin/constants/common';
import Button from 'admin/components/common/Button';
import { Modal } from 'admin/components/common/Modal';
import Text from 'admin/components/common/Text';
import Loader from 'common/components/Loader';
import type { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import type { InfographicSelection } from 'types/story';
import css from './InfographicSelectionModal.scss';

type Props = ModalManagerProvidedProps<MODAL.INFOGRAPHIC_SELECTION>;

export type InfographicSelectionModalData = {
	onSelect: (selection: InfographicSelection) => Promise<string>;
	onCancel?: () => void;
};

type PickerSession = {
	requestId: string;
	nonce: string;
	parentOrigin: string;
};

type PickerState = 'loading' | 'ready' | 'inserting' | 'error';

const integrationConfig = config.infographic;
const processedSelectionIds = new Set<string>();
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PICKER_READY_TIMEOUT = 15_000;

const createRandomValue = () => {
	const bytes = new Uint8Array(16);
	window.crypto.getRandomValues(bytes);
	return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

const createPickerSession = (): PickerSession => ({
	requestId: `storycards-${createRandomValue()}`,
	nonce: createRandomValue(),
	parentOrigin: window.location.origin,
});

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isUrlFrom = (value: unknown, origin: string, pathPattern: RegExp) => {
	if (typeof value !== 'string') return false;

	try {
		const url = new URL(value);
		return url.protocol === 'https:' && url.origin === origin && pathPattern.test(url.pathname);
	} catch {
		return false;
	}
};

const getSelection = (data: unknown, session: PickerSession): InfographicSelection | null => {
	if (!isRecord(data) || data.type !== 'STORYCARDS_INFOGRAPHIC_SELECTED' || data.protocolVersion !== 1) return null;
	const { payload } = data;
	if (data.requestId !== session.requestId || data.nonce !== session.nonce || !isRecord(payload)) return null;

	if ('html' in payload || 'script' in payload) return null;

	const { selectionId, projectId, editorUrl, publicId, publicUrl, name, publishedVersion, transparent } = payload;
	if (
		typeof selectionId !== 'string' ||
		!uuidPattern.test(selectionId) ||
		typeof projectId !== 'string' ||
		!uuidPattern.test(projectId) ||
		typeof publicId !== 'string' ||
		publicId.length === 0 ||
		publicId.length > 160 ||
		typeof name !== 'string' ||
		name.length > 300 ||
		typeof publishedVersion !== 'number' ||
		!Number.isSafeInteger(publishedVersion) ||
		publishedVersion < 0 ||
		typeof transparent !== 'boolean' ||
		typeof editorUrl !== 'string' ||
		typeof publicUrl !== 'string' ||
		!isUrlFrom(editorUrl, integrationConfig.editorOrigin, /^\/app\/editor\/[^/]+\/?$/) ||
		!isUrlFrom(publicUrl, integrationConfig.deliveryOrigin, /^\/p\/[^/]+\/[^/]+\/?$/)
	) {
		return null;
	}

	return { selectionId, projectId, editorUrl, publicId, publicUrl, name, publishedVersion, transparent };
};

const isPickerReady = (data: unknown, session: PickerSession) =>
	isRecord(data) &&
	data.type === 'STORYCARDS_INFOGRAPHICS_PICKER_READY' &&
	data.protocolVersion === 1 &&
	data.requestId === session.requestId &&
	data.nonce === session.nonce;

const getPickerUrl = (session: PickerSession) => {
	const url = new URL(integrationConfig.pickerPath, integrationConfig.pickerOrigin);
	url.search = new URLSearchParams({
		integration: 'storycards',
		protocol: '1',
		requestId: session.requestId,
		nonce: session.nonce,
		parentOrigin: session.parentOrigin,
	}).toString();
	return url.toString();
};

const postInsertionResult = (
	pickerWindow: Window,
	session: PickerSession,
	selectionId: string,
	result: { status: 'success'; elementId: string } | { status: 'error'; errorCode: 'ELEMENT_INSERT_FAILED' }
) => {
	pickerWindow.postMessage(
		{
			type: 'STORYCARDS_INFOGRAPHIC_INSERT_RESULT',
			protocolVersion: 1,
			requestId: session.requestId,
			nonce: session.nonce,
			selectionId,
			...result,
		},
		integrationConfig.pickerOrigin
	);
};

const InfographicSelectionModal: React.FC<Props> = props => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [session, setSession] = useState<PickerSession | null>(null);
	const [pickerState, setPickerState] = useState<PickerState>('loading');
	const [attempt, setAttempt] = useState(0);
	const { close, data, open } = props;

	useEffect(() => {
		if (!open) {
			setSession(null);
			return undefined;
		}

		const nextSession = createPickerSession();
		setSession(nextSession);
		setPickerState('loading');
		const readyTimeout = window.setTimeout(() => setPickerState('error'), PICKER_READY_TIMEOUT);

		const onMessage = (event: MessageEvent<unknown>) => {
			const isExpectedPicker =
				event.origin === integrationConfig.pickerOrigin && event.source === iframeRef.current?.contentWindow;
			if (!isExpectedPicker) return;

			if (!isRecord(event.data)) {
				console.warn('[infographic-picker] Rejected picker message');
				return;
			}

			switch (event.data.type) {
				case 'STORYCARDS_INFOGRAPHICS_PICKER_READY':
					if (isPickerReady(event.data, nextSession)) {
						window.clearTimeout(readyTimeout);
						setPickerState('ready');
						return;
					}
					console.warn('[infographic-picker] Rejected picker message');
					return;

				case 'STORYCARDS_INFOGRAPHIC_SELECTED': {
					const selection = getSelection(event.data, nextSession);
					if (!selection) {
						console.warn('[infographic-picker] Rejected picker message');
						return;
					}

					if (processedSelectionIds.has(selection.selectionId)) return;
					processedSelectionIds.add(selection.selectionId);
					window.clearTimeout(readyTimeout);
					setPickerState('inserting');

					const pickerWindow = iframeRef.current?.contentWindow;
					if (!pickerWindow) return;

					void data
						.onSelect(selection)
						.then(elementId => {
							postInsertionResult(pickerWindow, nextSession, selection.selectionId, {
								status: 'success',
								elementId,
							});
							close();
						})
						.catch(() => {
							postInsertionResult(pickerWindow, nextSession, selection.selectionId, {
								status: 'error',
								errorCode: 'ELEMENT_INSERT_FAILED',
							});
							setPickerState('ready');
						});
					return;
				}

				default:
					console.warn('[infographic-picker] Rejected picker message');
			}
		};

		window.addEventListener('message', onMessage);
		return () => {
			window.removeEventListener('message', onMessage);
			window.clearTimeout(readyTimeout);
		};
	}, [attempt, close, data, open]);

	const retry = () => setAttempt(value => value + 1);
	const onCancel = () => {
		data.onCancel?.();
		close();
	};

	return (
		<Modal
			theme="light"
			maskColor="black"
			open={open}
			destroyOnHidden
			transitionName=""
			title={<Text size={Text.size.subtitle} weight={Text.weight.semibold} compact text="Add an infographic" />}
			width={1024}
			onCancel={onCancel}
		>
			<div className={css.picker}>
				{session && pickerState !== 'error' && (
					<iframe
						key={session.requestId}
						ref={iframeRef}
						src={getPickerUrl(session)}
						title="Choose a Storycards infographic"
						className={cn(css.frame, { [css.frameHidden]: pickerState !== 'ready' })}
						sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
						referrerPolicy="strict-origin-when-cross-origin"
					/>
				)}

				{pickerState === 'loading' && (
					<div className={css.loading} role="status" aria-live="polite">
						<div className={css.loadingContent}>
							<Loader size="small" color="var(--ra-color-brand)" />
						</div>
					</div>
				)}

				{pickerState === 'inserting' && (
					<div className={css.inserting} role="status" aria-live="polite">
						<Loader size="small" color="var(--ra-color-brand)" />
						<Text compact>Adding infographic</Text>
					</div>
				)}

				{pickerState === 'error' && (
					<div className={css.error} role="alert">
						<Text size={Text.size.subheading} weight={Text.weight.semibold} compact>
							Infographics could not load
						</Text>
						<Text compact>Check your connection and try again.</Text>
						<div className={css.actions}>
							<Button size="medium" onClick={retry}>
								Try again
							</Button>
							<Button size="medium" view="secondary" onClick={onCancel}>
								Close
							</Button>
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default InfographicSelectionModal;
